import { utils, writeFile } from 'xlsx';
import { isArray } from 'class-validator';
import { DateTime } from 'luxon';

/**
 * Mixin that adds functions for exporting appointments to excel
 * @mixin
 * @displayName Export Appointments Mixin
 */
export default {
  data() {
    return {
      buildingExcelData: false,
      // If you are not passing rows to the exportToExcel function, you should have this property on your component
      exportableRows: []
    };
  },
  computed: {
    exportableFields() {
      return Object.keys(this.novaCore.ExportableFields);
    },
    warehouse() {
      return null; // Implement on parent.
    }
  },
  methods: {
    buildExcelData(rows) {
      const sorted = this.novaCore.sortBy(rows, 'start');

      return sorted.map(row => {
        let refNumberDisplayName = this.$getSettingValue(
          'referenceNumberDisplayName',
          row.dock.warehouse.settings
        );
        let completedT;
        let actualDuration;
        let onTimeMinutes;
        let modifiedRow = {};
        const militaryTimeEnabled = this.$isMilitaryTimeEnabled(row.dock.warehouse);

        const arrivedT = row.statusTimeline[this.novaCore.AppointmentStatus.Arrived]
          ? momentjs.tz(
              row.statusTimeline[this.novaCore.AppointmentStatus.Arrived],
              row.dock.warehouse.timezone
            )
          : null;

        if (row.statusTimeline[this.novaCore.AppointmentStatus.Completed]) {
          completedT = momentjs.tz(
            row.statusTimeline[this.novaCore.AppointmentStatus.Completed],
            row.dock.warehouse.timezone
          );
          actualDuration = momentjs.duration(completedT.diff(arrivedT)).asMinutes();
        }

        if (arrivedT) {
          onTimeMinutes = this.util.makeTimeDiff(
            row.start,
            arrivedT,
            row.dock.warehouse.timezone,
            'minutes'
          );
        }

        this._makeRowCell('CONFIRMATION_NUMBER', row.confirmationNumber, modifiedRow);
        this._makeRowCell('APPOINTMENT_ID', row.id, modifiedRow);
        this._makeRowCell('APPOINTMENT_DATE', momentjs(row.start).format('MM/DD/YY'), modifiedRow);
        this._makeRowCell(
          'APPOINTMENT_TIME',
          this.getTimeInWarehouseTimezone(
            row,
            this.novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPM,
            this.novaCore.LuxonDateTimeFormats.Extended24HrTime
          ),
          modifiedRow
        );
        this._makeRowCell(
          'APPOINTMENT_TIMEZONE',
          this.getTimeInWarehouseTimezone(
            row,
            this.novaCore.LuxonDateTimeFormats.IANANamedOffset,
            this.novaCore.LuxonDateTimeFormats.IANANamedOffset
          ),
          modifiedRow
        );
        this._makeRowCell('APPOINTMENT_TYPE', row.type, modifiedRow);
        this._makeRowCell(
          'SCHEDULED_DURATION',
          this.util.makeTimeDiff(row.start, row.end, row.dock.warehouse.timezone, 'minutes'),
          modifiedRow
        );
        this._makeRowCell('STATUS', row.status, modifiedRow);
        this._makeRowCell('ARRIVAL_DATE', arrivedT ? arrivedT.format('MM/DD/YY') : '', modifiedRow);
        this._makeRowCell(
          'ARRIVAL_TIME',
          arrivedT
            ? this.novaCore.formatDateTimeWithMilitarySupport(
                arrivedT.toISOString(),
                row.dock.warehouse.timezone,
                this.novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPMWithAbbreviatedNamedOffset,
                militaryTimeEnabled,
                this.novaCore.LuxonDateTimeFormats.Extended24HrTimeWithAbbreviatedNamedOffset
              )
            : '',
          modifiedRow
        );
        this._makeRowCell(
          'DEPARTURE_DATE',
          completedT ? completedT.format('MM/DD/YY') : '',
          modifiedRow
        );
        this._makeRowCell(
          'DEPARTURE_TIME',
          completedT
            ? this.novaCore.formatDateTimeWithMilitarySupport(
                completedT.toISOString(),
                row.dock.warehouse.timezone,
                this.novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPMWithAbbreviatedNamedOffset,
                militaryTimeEnabled,
                this.novaCore.LuxonDateTimeFormats.Extended24HrTimeWithAbbreviatedNamedOffset
              )
            : '',
          modifiedRow
        );
        this._makeRowCell(
          'DWELL_TIME',
          actualDuration ? Math.ceil(parseFloat(actualDuration)) : '',
          modifiedRow
        );
        this._makeRowCell(
          'ON_TIME',
          onTimeMinutes ? Math.ceil(parseFloat(onTimeMinutes)) : '',
          modifiedRow
        );
        this._makeRowInProgressStatus(row, modifiedRow);
        this._makeRowCell('WAREHOUSE', row.dock?.warehouse?.name, modifiedRow);
        this._makeRowCell('DOCK', row.dock?.name, modifiedRow);
        this._makeRowCell('LOAD_TYPE', row.loadType?.name, modifiedRow);
        this._makeRowCell('LOAD_TYPE_DIRECTION', row.loadType?.direction, modifiedRow);
        this._makeRowCell('RESCHEDULE_COUNT', this.novaCore.getReschedulesCount(row), modifiedRow);

        if (this.exportableFields.includes('REFERENCE_NUMBER')) {
          modifiedRow[refNumberDisplayName] = row.refNumber;
        }

        if (row.createdByUser?.id) {
          this._makeRowCell(
            'CREATED_BY',
            `${row.createdByUser.firstName} ${row.createdByUser.lastName}`,
            modifiedRow
          );

          this._makeRowCell(
            'CREATOR_TYPE',
            this.makeAppointmentCreatorType(row.createdByUser),
            modifiedRow
          );
        }

        this._makeRowCell(
          'CREATION_DATE',
          momentjs.tz(row.createDateTime, row.dock.warehouse.timezone).format('MM/DD/YY'),
          modifiedRow
        );

        if (row.lastChangedByUser?.id) {
          this._makeRowCell(
            'LAST_UPDATED_BY',
            `${row.lastChangedByUser.firstName} ${row.lastChangedByUser.lastName}`,
            modifiedRow
          );
        }

        this._makeRowCell(
          'LAST_UPDATE_DATE',
          momentjs.tz(row.lastChangedDateTime, row.dock.warehouse.timezone).format('MM/DD/YY'),
          modifiedRow
        );

        this._makeRowCell(
          'LAST_UPDATE_TIME',
          this.novaCore.formatDateTimeWithMilitarySupport(
            row.lastChangedDateTime,
            row.dock.warehouse.timezone,
            this.novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPM,
            militaryTimeEnabled,
            this.novaCore.LuxonDateTimeFormats.Extended24HrTime
          ),
          modifiedRow
        );

        this._makeRowCell('CARRIER_COMPANY', row.user?.company?.name, modifiedRow);
        this._makeRowCell(
          'CONTACT_NAME',
          row.user ? `${row.user.firstName} ${row.user.lastName}` : null,
          modifiedRow
        );
        this._makeRowCell('CONTACT_EMAIL', row.user?.email, modifiedRow);
        this._makeRowCell('CONTACT_PHONE', row.user?.phone, modifiedRow);
        this._makeRowCell('NOTES', row.notes, modifiedRow);
        this._makeRowCell(
          'TAGS',
          !row.tags ? null : row.tags.map(t => `#${t}`).join(', '),
          modifiedRow
        );

        if (Object.values(this.exportableFields).includes('CUSTOM_FIELDS')) {
          this._makeRowCustomFields(row, modifiedRow);
        }

        return modifiedRow;
      });
    },
    _makeRowCustomFields(row, modifiedRow) {
      if (Array.isArray(row.customFields)) {
        const fieldCount = {};
        row.customFields.forEach(cField => {
          let v = cField.value;

          // Drop-down multi select store the value as array
          if (isArray(cField.value)) {
            v = cField.value.join(', ');
          }

          if (cField.type === 'int' && cField.value) {
            v = parseInt(cField.value);
          }

          if (cField.type === 'doc') {
            if (cField.value) {
              v = {
                t: 's',
                v: this.novaCore.getDocumentNameFromUrl(cField.value),
                l: {
                  Target: cField.value,
                  Tooltip: 'Download File'
                } // Target is the URL, optionally specify Tooltip
              };
            }
          }
          if (modifiedRow[cField.label]) {
            fieldCount[cField.label] = (fieldCount[cField.label] || 1) + 1;
            modifiedRow[`${cField.label} (${fieldCount[cField.label]})`] = v;
          } else {
            fieldCount[cField.label] = 1;
            modifiedRow[cField.label] = v;
          }
        });
      }
    },
    _makeRowCell(key, value, row) {
      if (this.exportableFields.includes(key)) {
        row[this.novaCore.ExportableFields[key]] = value;
      }
    },
    _makeRowInProgressStatus(row, modifiedRow) {
      const militaryTimeEnabled = this.$isMilitaryTimeEnabled(this.$org);

      const isInProgressStatusInUse =
        this.$getSettingValue('statusSucceedingArrived', this.warehouse?.settings) ===
        this.novaCore.AppointmentStatus.InProgress;

      let arrivedTimeStamp;
      let inProgressTimeStamp;
      let completedTimeStamp;

      if (isInProgressStatusInUse) {
        arrivedTimeStamp = row.statusTimeline[this.novaCore.AppointmentStatus.Arrived];
        inProgressTimeStamp = row.statusTimeline[this.novaCore.AppointmentStatus.InProgress];
        completedTimeStamp = row.statusTimeline[this.novaCore.AppointmentStatus.Completed];
      }

      this._makeRowCell(
        'IN_PROGRESS_DATE',
        inProgressTimeStamp
          ? DateTime.fromISO(inProgressTimeStamp, { setZone: true }).toFormat(
              this.novaCore.LuxonDateTimeFormats.MonthDayYearSlashed
            )
          : '',
        modifiedRow
      );

      this._makeRowCell(
        'IN_PROGRESS_TIME',
        inProgressTimeStamp
          ? this.novaCore.formatDateTimeWithMilitarySupport(
              inProgressTimeStamp,
              row.dock.warehouse.timezone,
              this.novaCore.LuxonDateTimeFormats.Extended12HrTimeAMPM,
              militaryTimeEnabled,
              this.novaCore.LuxonDateTimeFormats.Extended24HrTime
            )
          : '',
        modifiedRow
      );

      let waitingTime = '';

      if (arrivedTimeStamp && inProgressTimeStamp) {
        waitingTime = this.util.makeTimeDiff(
          arrivedTimeStamp,
          inProgressTimeStamp,
          row.dock.warehouse.timezone,
          'minutes'
        );
      }
      this._makeRowCell('WAIT_TIME', waitingTime, modifiedRow);

      let processingTime = '';
      if (completedTimeStamp && inProgressTimeStamp) {
        processingTime = this.util.makeTimeDiff(
          inProgressTimeStamp,
          completedTimeStamp,
          row.dock.warehouse.timezone,
          'minutes'
        );
      }
      this._makeRowCell('PROCESSING_TIME', processingTime, modifiedRow);
    },
    exportToExcel(rows = null, fileName = 'opendock_appointments.xlsx') {
      const exportRows = rows || this.exportableRows;
      this.buildingExcelData = true;
      const data = this.buildExcelData(exportRows);
      const wb = utils.book_new();

      utils.book_append_sheet(wb, utils.json_to_sheet(data), 'Appointments');

      writeFile(wb, fileName);

      this.mixpanel.track(this.mixpanel.events.ACTION.APPOINTMENTS_EXPORTED, {
        'Org Name': this.$org?.name,
        'Org ID': this.$org?.id,
        'Report Fields': this.exportableFields
      });

      this.buildingExcelData = false;
    }
  }
};
